/**
 * LogSheet — the primary interaction of the whole module.
 *
 * A bottom sheet with one screen per thing worth logging. The PRD's acceptance criterion
 * is literal and worth keeping in front of you while editing this file:
 *
 *     "If logging blood pressure takes more than five seconds, this module has failed."
 *
 * So: the sheet opens straight onto the action list, every action is one tap from there,
 * the first field of every form is autofocused, and numeric fields carry inputMode so a
 * phone shows a number pad instead of a keyboard.
 *
 * NOTHING IN HERE SCOLDS. No streak language, no red X for a missed dose, no "you're
 * behind". A thing not yet logged is simply not yet logged. The module exists because
 * shame around inconsistency made the inconsistency invisible; rebuilding that in software
 * would defeat it.
 */
import { useEffect, useRef, useState } from 'react';
import { api, ApiRequestError } from '../../lib/api';
import type {
  WellnessDailyStatus,
  WellnessMedication,
  WellnessMetricType,
  WellnessRecipe,
  WellnessSessionType,
} from '../../shared/types';
import { Icon } from '../home/ui';
import { SESSION_LABELS, macro, toStored } from './format';

/**
 * `metric:<slug>` rather than one named screen per metric.
 *
 * Sleep was showing "not yet" on the Today card with NO WAY TO LOG IT, because the menu
 * hardcoded four buttons while the card derived its dots from `daily_expected`. The two
 * disagreed the moment a fifth metric existed. The menu is now derived from the same
 * config, so a metric that can be asked for can always be answered.
 */
type Screen = 'menu' | 'meds' | 'meal' | 'workout' | `metric:${string}`;

export default function LogSheet({
  open,
  onClose,
  onLogged,
  status,
  medications,
  metricTypes,
}: {
  open: boolean;
  onClose: () => void;
  /** Called after any successful write so the caller can refetch. */
  onLogged: () => void;
  status: WellnessDailyStatus | null;
  medications: WellnessMedication[];
  metricTypes: WellnessMetricType[];
}) {
  const [screen, setScreen] = useState<Screen>('menu');

  // Always reopen on the menu. Reopening onto whatever was last used would mean the
  // common case (log a pressure) sometimes costs an extra tap to get back out of.
  useEffect(() => {
    if (open) setScreen('menu');
  }, [open]);

  // Escape closes, and the body doesn't scroll behind the sheet.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const done = () => {
    onLogged();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-ink/30 backdrop-blur-[2px]"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Log"
        className="relative max-h-[88vh] w-full overflow-y-auto rounded-t-3xl bg-surface p-5 shadow-lg sm:max-w-lg sm:rounded-3xl sm:p-6"
      >
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-ink">
            {screen === 'menu' ? 'Log' : titleFor(screen, metricTypes)}
          </h2>
          <div className="flex items-center gap-1">
            {screen !== 'menu' && (
              <button
                type="button"
                onClick={() => setScreen('menu')}
                className="inline-flex min-h-11 items-center rounded-full px-3 text-sm font-medium text-ink-soft hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
              >
                Back
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full text-ink-soft hover:bg-canvas hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
            >
              <Icon name="arrow" className="h-5 w-5 rotate-90" />
            </button>
          </div>
        </div>

        {screen === 'menu' && (
          <Menu status={status} medications={medications} types={metricTypes} go={setScreen} />
        )}
        {screen === 'meds' && (
          <MedsScreen medications={medications} status={status} onDone={done} />
        )}
        {screen === 'meal' && <MealScreen onDone={done} />}
        {screen === 'workout' && <WorkoutScreen onDone={done} />}
        {screen.startsWith('metric:') &&
          (() => {
            const slug = screen.slice('metric:'.length);
            const type = metricTypes.find((t) => t.slug === slug);
            if (!type) return <p className="text-sm text-ink-soft">That metric isn’t set up.</p>;
            // Blood pressure needs two boxes; weight needs the lb→g conversion. Everything
            // else is one number in the unit it displays, so it shares one screen.
            if (type.value_shape === 'bp') return <PressureScreen type={type} onDone={done} />;
            if (type.slug === 'weight') return <WeightScreen type={type} onDone={done} />;
            return <MetricScreen type={type} onDone={done} />;
          })()}
      </div>
    </div>
  );
}

function titleFor(screen: Screen, types: WellnessMetricType[]): string {
  if (screen === 'meds') return 'Medication';
  if (screen === 'meal') return 'Meal';
  if (screen === 'workout') return 'Workout';
  const slug = screen.slice('metric:'.length);
  return types.find((t) => t.slug === slug)?.label ?? 'Log';
}

// ---------------------------------------------------------------- menu

function Menu({
  status,
  medications,
  types,
  go,
}: {
  status: WellnessDailyStatus | null;
  medications: WellnessMedication[];
  types: WellnessMetricType[];
  go: (s: Screen) => void;
}) {
  // The subtitle states a fact ("2 of 3 logged"), never a judgement.
  const medItem = status?.items.filter((i) => i.key.startsWith('med:') || i.key === 'supplements');
  const medDone = medItem?.reduce((n, i) => n + i.done, 0) ?? 0;
  const medExpected = medItem?.reduce((n, i) => n + i.expected, 0) ?? 0;

  type Action = { screen: Screen; label: string; sub: string; icon: 'check' | 'pulse' | 'coin' | 'pot' | 'broom' };

  const actions: Action[] = [
    {
      screen: 'meds',
      label: medications.length ? 'Medication and supplements' : 'Medication',
      sub: medExpected ? `${medDone} of ${medExpected} logged today` : 'Nothing set up yet',
      icon: 'check',
    },
  ];

  // EVERY metric the day expects gets an entry, derived from the same daily_expected the
  // Today card reads. Adding a metric in admin puts it here with no code change — and a
  // metric can never again be asked for on the card but unloggable in the sheet.
  for (const t of types.filter((t) => t.daily_expected > 0)) {
    const item = status?.items.find((i) => i.key === `metric:${t.slug}`);
    actions.push({
      screen: `metric:${t.slug}`,
      label: t.label,
      sub: item
        ? `${item.done} of ${item.expected} today`
        : t.slug === 'weight'
          ? 'In pounds'
          : `In ${t.unit}`,
      icon: t.value_shape === 'bp' ? 'pulse' : 'coin',
    });
  }

  actions.push(
    { screen: 'meal', label: 'Meal', sub: 'From your library, or by hand', icon: 'pot' },
    { screen: 'workout', label: 'Workout', sub: 'Type, time, heart rate', icon: 'broom' }
  );

  return (
    <ul className="space-y-2">
      {actions.map((a) => (
        <li key={a.screen}>
          <button
            type="button"
            onClick={() => go(a.screen)}
            className="flex min-h-[3.75rem] w-full items-center gap-3 rounded-2xl border border-border bg-canvas px-4 py-3 text-left hover:border-sage focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface">
              <Icon name={a.icon} className="h-5 w-5 text-ink-soft" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-medium text-ink">{a.label}</span>
              <span className="block text-sm text-ink-soft">{a.sub}</span>
            </span>
            <Icon name="arrow" className="h-4 w-4 shrink-0 text-ink-faint" />
          </button>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------- shared form bits

function useSubmit(onDone: () => void) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (fn: () => Promise<unknown>) => {
    setSaving(true);
    setError(null);
    try {
      await fn();
      onDone();
    } catch (e) {
      // The server's `detail` is written for a person — show it rather than a generic
      // failure, so a rejected value says what was wrong with it.
      setError(
        e instanceof ApiRequestError && e.body.detail
          ? e.body.detail
          : 'That didn’t save. Try again?'
      );
    } finally {
      setSaving(false);
    }
  };

  return { saving, error, run };
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-ink">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-ink-soft">{hint}</span>}
    </label>
  );
}

const inputClass =
  'min-h-11 w-full rounded-xl border border-border bg-canvas px-3 py-2 text-ink placeholder:text-ink-faint focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage';

function SaveBar({
  saving,
  error,
  label = 'Save',
}: {
  saving: boolean;
  error: string | null;
  label?: string;
}) {
  return (
    <>
      {error && (
        <p role="alert" className="rounded-xl bg-clay-soft px-3 py-2 text-sm text-clay">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={saving}
        className="min-h-12 w-full rounded-full bg-sage px-4 font-medium text-white disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
      >
        {saving ? 'Saving…' : label}
      </button>
    </>
  );
}

// ---------------------------------------------------------------- medication

/**
 * Prescriptions are logged individually; supplements go in one tap.
 *
 * That split is the decision from 16 Aug 2026 and it is not cosmetic: prescription
 * adherence is the number that goes to a doctor, so it gets an honest per-item yes/no,
 * while four taps a day for supplements is the friction that made the original record
 * disappear. `as_needed` items are listed but never counted as missed.
 */
function MedsScreen({
  medications,
  status,
  onDone,
}: {
  medications: WellnessMedication[];
  status: WellnessDailyStatus | null;
  onDone: () => void;
}) {
  const { saving, error, run } = useSubmit(onDone);
  const prescriptions = medications.filter((m) => m.category === 'prescription');
  const supplements = medications.filter(
    (m) => m.category === 'supplement' && m.schedule !== 'as_needed'
  );
  const asNeeded = medications.filter((m) => m.schedule === 'as_needed');

  const doneFor = (id: number) => status?.items.find((i) => i.key === `med:${id}`)?.done === 1;
  const stack = status?.items.find((i) => i.key === 'supplements');

  if (!medications.length) {
    return (
      <p className="rounded-xl bg-canvas px-4 py-6 text-center text-sm text-ink-soft">
        No medications or supplements set up yet.
      </p>
    );
  }

  return (
    <div className="space-y-5">
      {prescriptions.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-xs font-semibold tracking-wide text-ink-soft uppercase">
            Prescription
          </h3>
          {prescriptions.map((m) => (
            <div key={m.id} className="rounded-2xl border border-border bg-canvas p-3">
              <div className="mb-2">
                <p className="font-medium text-ink">
                  {m.name}
                  {m.dose && <span className="ml-2 text-sm text-ink-soft">{m.dose}</span>}
                </p>
                {m.anchor && <p className="text-xs text-ink-soft">by the {m.anchor}</p>}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={saving}
                  onClick={() =>
                    run(() => api.post(`/wellness/medications/${m.id}/log`, { status: 'taken' }))
                  }
                  className={`min-h-11 flex-1 rounded-full px-3 text-sm font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage ${
                    doneFor(m.id) ? 'bg-sage text-white' : 'border border-sage bg-surface text-sage'
                  }`}
                >
                  {doneFor(m.id) ? 'Taken ✓' : 'Took it'}
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() =>
                    run(() => api.post(`/wellness/medications/${m.id}/log`, { status: 'skipped' }))
                  }
                  className="min-h-11 rounded-full border border-border bg-surface px-4 text-sm font-medium text-ink-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
                >
                  Skipped
                </button>
              </div>
            </div>
          ))}
        </section>
      )}

      {supplements.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-xs font-semibold tracking-wide text-ink-soft uppercase">
            Supplements
          </h3>
          <div className="rounded-2xl border border-border bg-canvas p-3">
            <p className="mb-1 text-sm text-ink">{supplements.map((m) => m.name).join(', ')}</p>
            <p className="mb-3 text-xs text-ink-soft">
              {stack ? `${stack.done} of ${stack.expected} logged today` : 'Not logged today'}
            </p>
            <button
              type="button"
              disabled={saving}
              onClick={() => run(() => api.post('/wellness/medications/log-stack', {}))}
              className={`min-h-11 w-full rounded-full px-3 text-sm font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage ${
                stack && stack.done >= stack.expected
                  ? 'bg-sage text-white'
                  : 'border border-sage bg-surface text-sage'
              }`}
            >
              {stack && stack.done >= stack.expected ? 'Stack taken ✓' : 'Took my stack'}
            </button>
          </div>
        </section>
      )}

      {asNeeded.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-xs font-semibold tracking-wide text-ink-soft uppercase">
            As needed
          </h3>
          {asNeeded.map((m) => (
            <div
              key={m.id}
              className="flex items-center gap-3 rounded-2xl border border-border bg-canvas p-3"
            >
              <span className="min-w-0 flex-1 text-sm text-ink">{m.name}</span>
              <button
                type="button"
                disabled={saving}
                onClick={() =>
                  run(() => api.post(`/wellness/medications/${m.id}/log`, { status: 'taken' }))
                }
                className="min-h-11 shrink-0 rounded-full border border-sage bg-surface px-4 text-sm font-medium text-sage focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
              >
                Took it
              </button>
            </div>
          ))}
          {/* Stated plainly so an empty row here never reads as a failure. */}
          <p className="text-xs text-ink-soft">Not counted as missed when unlogged.</p>
        </section>
      )}

      {error && (
        <p role="alert" className="rounded-xl bg-clay-soft px-3 py-2 text-sm text-clay">
          {error}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- pressure

function PressureScreen({ type, onDone }: { type: WellnessMetricType; onDone: () => void }) {
  const { saving, error, run } = useSubmit(onDone);
  const [systolic, setSystolic] = useState('');
  const [diastolic, setDiastolic] = useState('');
  const [pulse, setPulse] = useState('');
  const [context, setContext] = useState<'morning' | 'evening'>(
    new Date().getHours() < 14 ? 'morning' : 'evening'
  );
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => first.current?.focus(), []);

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        run(() =>
          api.post('/wellness/readings', {
            metric_type_id: type.id,
            value: { systolic: Number(systolic), diastolic: Number(diastolic), pulse: pulse || null },
            context,
          })
        );
      }}
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Systolic">
          <input
            ref={first}
            className={inputClass}
            inputMode="numeric"
            required
            value={systolic}
            onChange={(e) => setSystolic(e.target.value)}
            placeholder="134"
          />
        </Field>
        <Field label="Diastolic">
          <input
            className={inputClass}
            inputMode="numeric"
            required
            value={diastolic}
            onChange={(e) => setDiastolic(e.target.value)}
            placeholder="88"
          />
        </Field>
      </div>
      <Field label="Pulse" hint="Optional.">
        <input
          className={inputClass}
          inputMode="numeric"
          value={pulse}
          onChange={(e) => setPulse(e.target.value)}
          placeholder="66"
        />
      </Field>
      <fieldset>
        <legend className="mb-1 text-sm font-medium text-ink">When</legend>
        <div className="flex gap-2">
          {(['morning', 'evening'] as const).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setContext(c)}
              aria-pressed={context === c}
              className={`min-h-11 flex-1 rounded-full px-3 text-sm font-medium capitalize focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage ${
                context === c
                  ? 'bg-sage text-white'
                  : 'border border-border bg-canvas text-ink-soft'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </fieldset>
      <SaveBar saving={saving} error={error} />
    </form>
  );
}

// ---------------------------------------------------------------- weight

function WeightScreen({ type, onDone }: { type: WellnessMetricType; onDone: () => void }) {
  const { saving, error, run } = useSubmit(onDone);
  const [lb, setLb] = useState('');
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => first.current?.focus(), []);

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        // Typed in pounds, STORED IN GRAMS. toStored() is the only conversion path.
        run(() =>
          api.post('/wellness/readings', {
            metric_type_id: type.id,
            value: { v: toStored(Number(lb), 'weight') },
          })
        );
      }}
    >
      <Field label="Weight" hint="Pounds. Stored to the gram, so decimals are kept.">
        <input
          ref={first}
          className={inputClass}
          inputMode="decimal"
          required
          value={lb}
          onChange={(e) => setLb(e.target.value)}
          placeholder="213.4"
        />
      </Field>
      <SaveBar saving={saving} error={error} />
    </form>
  );
}

// ---------------------------------------------------------------- any other metric

/**
 * One number, in the unit the metric declares. Sleep, waist, resting heart rate, VO2 max —
 * anything whose value_shape is a plain scalar or a duration.
 *
 * This is what was missing: sleep expects a reading every day, so the Today card asked for
 * it, but there was no screen to answer with. A generic screen means the next metric added
 * in admin is loggable the moment it exists.
 */
function MetricScreen({ type, onDone }: { type: WellnessMetricType; onDone: () => void }) {
  const { saving, error, run } = useSubmit(onDone);
  const [v, setV] = useState('');
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => first.current?.focus(), []);

  const band =
    type.target_low !== null && type.target_high !== null
      ? `Target ${type.target_low}–${type.target_high} ${type.unit}.`
      : type.target_low !== null
        ? `Target at least ${type.target_low} ${type.unit}.`
        : type.target_high !== null
          ? `Target under ${type.target_high} ${type.unit}.`
          : undefined;

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        run(() =>
          api.post('/wellness/readings', {
            metric_type_id: type.id,
            value: { v: Number(v) },
          })
        );
      }}
    >
      <Field
        label={`${type.label} (${type.unit})`}
        hint={
          type.value_shape === 'duration'
            ? `Hours, decimals fine — 7.5 for seven and a half.${band ? ` ${band}` : ''}`
            : band
        }
      >
        <input
          ref={first}
          className={inputClass}
          inputMode="decimal"
          required
          value={v}
          onChange={(e) => setV(e.target.value)}
          placeholder={type.value_shape === 'duration' ? '7.5' : ''}
        />
      </Field>
      <SaveBar saving={saving} error={error} />
    </form>
  );
}

// ---------------------------------------------------------------- meal

/**
 * Manual macro entry. The AI resolver lands behind the same POST later — this form stays
 * as the fallback and as the edit surface for whatever the resolver returns.
 *
 * EVERY MACRO FIELD IS OPTIONAL AND BLANK MEANS NULL. The placeholder is a hint, never a
 * default, and nothing here coerces an empty box to 0.
 */
function MealScreen({ onDone }: { onDone: () => void }) {
  const { saving, error, run } = useSubmit(onDone);
  const [recipes, setRecipes] = useState<WellnessRecipe[] | null>(null);
  const [manual, setManual] = useState(false);
  const [q, setQ] = useState('');
  const [description, setDescription] = useState('');
  const [m, setM] = useState<Record<string, string>>({});

  // Library first, because the whole point of putting macros on a recipe once is that
  // logging it later costs one tap instead of six fields.
  useEffect(() => {
    api
      .get<WellnessRecipe[]>('/wellness/recipes')
      .then(setRecipes)
      .catch(() => setRecipes([]));
  }, []);

  const set = (k: string, v: string) => setM((prev) => ({ ...prev, [k]: v }));
  const fields: [string, string, string][] = [
    ['calories', 'Calories', '520'],
    ['protein_g', 'Protein (g)', '46'],
    ['fat_g', 'Fat (g)', '32'],
    ['carb_g', 'Carbs (g)', '9'],
    ['sodium_mg', 'Sodium (mg)', '140'],
    ['potassium_mg', 'Potassium (mg)', '1100'],
  ];

  if (!manual) {
    const shown = (recipes ?? []).filter((r) =>
      r.name.toLowerCase().includes(q.toLowerCase())
    );
    return (
      <div className="space-y-3">
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search your meals"
          className={inputClass}
        />

        {recipes === null && <p className="text-sm text-ink-soft">Loading your meals…</p>}

        {recipes !== null && shown.length === 0 && (
          <p className="px-1 py-4 text-center text-sm text-ink-soft">
            {recipes.length === 0
              ? 'Your meal library is empty. Add meals from the Meals tab and they show up here.'
              : 'Nothing matches that.'}
          </p>
        )}

        <ul className="space-y-1.5">
          {shown.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                disabled={saving}
                onClick={() => run(() => api.post(`/wellness/recipes/${r.id}/log`, {}))}
                className="w-full rounded-xl border border-border bg-canvas px-3 py-2.5 text-left hover:border-sage focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
              >
                <span className="block font-medium text-ink">{r.name}</span>
                {/* Shows what will actually land in the day's totals, blanks included. */}
                <span className="block text-xs text-ink-soft">
                  {macro(r.calories)} kcal · {macro(r.protein_g, 'g')} protein ·{' '}
                  {macro(r.sodium_mg, 'mg')} sodium
                </span>
              </button>
            </li>
          ))}
        </ul>

        {error && (
          <p role="alert" className="rounded-xl bg-clay-soft px-3 py-2 text-sm text-clay">
            {error}
          </p>
        )}

        <button
          type="button"
          onClick={() => setManual(true)}
          className="min-h-11 w-full rounded-full border border-border bg-surface px-4 text-sm font-medium text-ink-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
        >
          Something else — enter it by hand
        </button>
      </div>
    );
  }

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        run(() =>
          api.post('/wellness/meals', {
            description,
            // '' is sent through as-is; the Worker's num() turns it into null, never 0.
            ...Object.fromEntries(fields.map(([k]) => [k, m[k] ?? ''])),
          })
        );
      }}
    >
      <Field label="What you ate">
        <input
          autoFocus
          className={inputClass}
          required
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="6 oz sirloin, half an avocado, cup of spinach"
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        {fields.map(([key, label, placeholder]) => (
          <Field key={key} label={label}>
            <input
              className={inputClass}
              inputMode="numeric"
              value={m[key] ?? ''}
              onChange={(e) => set(key, e.target.value)}
              placeholder={placeholder}
            />
          </Field>
        ))}
      </div>

      <p className="text-xs text-ink-soft">
        Leave anything you don’t know blank — it stays blank rather than counting as zero.
      </p>
      <SaveBar saving={saving} error={error} label="Save meal" />
      <button
        type="button"
        onClick={() => setManual(false)}
        className="min-h-11 w-full text-sm text-ink-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
      >
        Back to your meals
      </button>
    </form>
  );
}

// ---------------------------------------------------------------- workout

function WorkoutScreen({ onDone }: { onDone: () => void }) {
  const { saving, error, run } = useSubmit(onDone);
  const [type, setType] = useState<WellnessSessionType>('strength');
  const [duration, setDuration] = useState('');
  const [avgHr, setAvgHr] = useState('');
  const [maxHr, setMaxHr] = useState('');
  const [notes, setNotes] = useState('');

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        run(() =>
          api.post('/wellness/sessions', {
            session_type: type,
            duration_min: duration,
            avg_hr: avgHr,
            max_hr: maxHr,
            notes,
          })
        );
      }}
    >
      <fieldset>
        <legend className="mb-1 text-sm font-medium text-ink">Type</legend>
        <div className="flex flex-wrap gap-2">
          {(Object.keys(SESSION_LABELS) as WellnessSessionType[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setType(t)}
              aria-pressed={type === t}
              className={`min-h-11 rounded-full px-4 text-sm font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage ${
                type === t ? 'bg-sage text-white' : 'border border-border bg-canvas text-ink-soft'
              }`}
            >
              {SESSION_LABELS[t]}
            </button>
          ))}
        </div>
      </fieldset>

      <div className="grid grid-cols-3 gap-3">
        <Field label="Minutes">
          <input
            className={inputClass}
            inputMode="numeric"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            placeholder="48"
          />
        </Field>
        <Field label="Avg HR">
          <input
            className={inputClass}
            inputMode="numeric"
            value={avgHr}
            onChange={(e) => setAvgHr(e.target.value)}
            placeholder="112"
          />
        </Field>
        <Field label="Max HR">
          <input
            className={inputClass}
            inputMode="numeric"
            value={maxHr}
            onChange={(e) => setMaxHr(e.target.value)}
            placeholder="141"
          />
        </Field>
      </div>

      <Field label="Notes" hint="Optional — what you actually did.">
        <input
          className={inputClass}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="full body"
        />
      </Field>

      <SaveBar saving={saving} error={error} label="Save workout" />
    </form>
  );
}
